/**
 * Staff portal — My Policies
 *
 * Mirrors [admin/policies/index.tsx]:
 *  - Header with Title + dimmed subtitle and refresh ActionIcon
 *  - 4 clickable Stats cards (To Sign / Overdue / Signed / All Published)
 *    that filter the list — clicking again clears the filter.
 *  - Search + persistent view-mode toggle (`hr.my.policies`)
 *  - DataTable + Item + Grid views
 *
 * Data shape: we pull both `listPublished()` (every policy a staff member can
 * read) and `getMyAssignments()` (the rows that obligate them to sign), then
 * project a unified row list. The "To Sign / Signed" filters use the
 * assignment status; the unfiltered list shows every policy with the user's
 * personal status alongside.
 */

import { useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Group,
  Card,
  SimpleGrid,
  ThemeIcon,
  Paper,
  Button,
  Tooltip,
  SegmentedControl,
  Grid,
  Alert,
  Loader,
  Divider,
  Badge,
} from '@/components/ui';
import { TextInput } from '@/components/ui/inputs';
import ActionIcon from '@/components/ui/ActionIcon';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import {
  IconRefresh, IconEye, IconBookmark, IconShieldCheck, IconAlertCircle,
  IconHourglassEmpty, IconExclamationCircle, IconCircleCheck, IconLayoutGrid,
  IconLayoutList, IconLayoutRows, IconSearch, IconHistory, IconCalendar,
  IconSignature, IconBookmarks,
} from '@tabler/icons-react';
import {
  useStaffPortalPolicyAssignmentService,
  useStaffPortalPolicyService,
  type Policy,
  type PolicyAssignment,
} from '@actschurch/ccms-sdk';
import { useViewMode } from '@/stores/viewModeStore';
import { hrKeys } from '../utils/queryKeys';
import { PolicyAssignmentStatusBadge } from '../components';
import { formatDate } from '../utils/hrHelpers';
import { POLICY_ASSIGNMENT_STATUS } from '../utils/hrConstants';

/** Unified row blending a Policy with its (optional) assignment for the user. */
type PolicyRow = {
  id: number;
  policy: Policy;
  assignment: PolicyAssignment | null;
  /** Derived bucket — used for stats + filters. */
  bucket: 'to-sign' | 'overdue' | 'signed' | 'browse';
};

type PoliciesViewMode = 'list' | 'item' | 'grid';
type StatsFilter = 'all' | 'to-sign' | 'overdue' | 'signed' | null;

export default function MyPoliciesPage() {
  const navigate = useNavigate();
  const assignmentService = useStaffPortalPolicyAssignmentService();
  const policyService = useStaffPortalPolicyService();

  const [viewMode, setViewMode] = useViewMode<PoliciesViewMode>('hr.my.policies', 'list');
  const [search, setSearch] = useState('');
  const [statsFilter, setStatsFilter] = useState<StatsFilter>(null);

  const {
    data: assignments = [],
    isLoading: aLoading,
    isFetching: aFetching,
    error: aError,
    refetch: refetchAssignments,
  } = useQuery({
    queryKey: hrKeys.myAssignments(),
    queryFn: () => assignmentService.getMyAssignments(),
  });

  const {
    data: published = [],
    isLoading: pLoading,
    isFetching: pFetching,
    error: pError,
    refetch: refetchPublished,
  } = useQuery({
    queryKey: hrKeys.publishedPolicies(),
    queryFn: () => policyService.listPublished(),
  });

  const refresh = () => { refetchAssignments(); refetchPublished(); };
  const loading = aLoading || aFetching || pLoading || pFetching;

  const allRows = useMemo<PolicyRow[]>(() => {
    const assignmentByPolicy = new Map<number, PolicyAssignment>();
    (assignments as PolicyAssignment[]).forEach((a) => {
      // If the user has multiple assignments for the same policy (e.g. an
      // older Superseded one), keep the most recently assigned.
      const existing = assignmentByPolicy.get(a.policyID);
      if (!existing || (a.assignedDateTime ?? '') > (existing.assignedDateTime ?? '')) {
        assignmentByPolicy.set(a.policyID, a);
      }
    });

    const rows = (published as Policy[]).map((p) => {
      const a = assignmentByPolicy.get(p.policyID) ?? null;
      let bucket: PolicyRow['bucket'] = 'browse';
      if (a) {
        if (a.status === POLICY_ASSIGNMENT_STATUS.OVERDUE) bucket = 'overdue';
        else if (a.status === POLICY_ASSIGNMENT_STATUS.PENDING) bucket = 'to-sign';
        else if (a.status === POLICY_ASSIGNMENT_STATUS.SIGNED) bucket = 'signed';
      }
      return { id: p.policyID, policy: p, assignment: a, bucket };
    });

    // Sort: actionable first (overdue, then to-sign), then signed, then browse
    // — within each bucket, by due-date asc when present, otherwise name.
    const order: Record<PolicyRow['bucket'], number> = {
      overdue: 0, 'to-sign': 1, signed: 2, browse: 3,
    };
    rows.sort((a, b) => {
      if (order[a.bucket] !== order[b.bucket]) return order[a.bucket] - order[b.bucket];
      const aDue = a.assignment?.dueDate ? new Date(a.assignment.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      const bDue = b.assignment?.dueDate ? new Date(b.assignment.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      if (aDue !== bDue) return aDue - bDue;
      return (a.policy.name ?? '').localeCompare(b.policy.name ?? '');
    });
    return rows;
  }, [assignments, published]);

  const stats = useMemo(() => ({
    total: allRows.length,
    toSign: allRows.filter((r) => r.bucket === 'to-sign').length,
    overdue: allRows.filter((r) => r.bucket === 'overdue').length,
    signed: allRows.filter((r) => r.bucket === 'signed').length,
  }), [allRows]);

  const filtered = useMemo(() => {
    let rows = allRows;
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        `${r.policy.name ?? ''} ${r.policy.description ?? ''} ${r.policy.version ?? ''} #${r.policy.policyID}`.toLowerCase().includes(q),
      );
    }
    if (statsFilter === 'to-sign') rows = rows.filter((r) => r.bucket === 'to-sign');
    else if (statsFilter === 'overdue') rows = rows.filter((r) => r.bucket === 'overdue');
    else if (statsFilter === 'signed') rows = rows.filter((r) => r.bucket === 'signed');
    return rows;
  }, [allRows, search, statsFilter]);

  const handleStatsClick = useCallback((filter: StatsFilter) => {
    setStatsFilter((prev) => (prev === filter || filter === 'all' ? null : filter));
  }, []);

  /** All routes use the policyID — the detail page handles the optional assignment. */
  const handleView = useCallback((policyId: number) => navigate(`${policyId}`), [navigate]);

  const renderStatusBadge = (r: PolicyRow) => {
    if (r.assignment) return <PolicyAssignmentStatusBadge status={r.assignment.status} />;
    return <Badge size="sm" variant="light" color="gray">Not assigned</Badge>;
  };

  const columns: DataTableColumn<PolicyRow>[] = useMemo(() => [
    {
      key: 'name',
      label: 'Policy',
      sortable: true,
      render: (_v, r) => (
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon
            size="md"
            variant="light"
            color={r.policy.requireSignatory ? 'orange' : 'blue'}
          >
            {r.policy.requireSignatory ? <IconSignature size={16} /> : <IconBookmark size={16} />}
          </ThemeIcon>
          <Stack gap={0}>
            <Text size="sm" fw={500} truncate maw={320}>{r.policy.name ?? `Policy #${r.policy.policyID}`}</Text>
            <Group gap={6}>
              <Text size="xs" c="dimmed">#{r.policy.policyID}</Text>
              {r.policy.version && <Text size="xs" c="dimmed">v{r.policy.version}</Text>}
            </Group>
          </Stack>
        </Group>
      ),
    },
    {
      // Dotted key so DataTable.getValue() reaches into the nested policy —
      // the row is a PolicyRow, the date lives at row.policy.effectiveDate.
      key: 'policy.effectiveDate',
      label: 'Effective',
      sortable: true,
      width: 130,
      render: (v) => <Text size="sm">{formatDate(v as string | null)}</Text>,
    },
    {
      key: 'dueDate',
      label: 'Due',
      width: 130,
      render: (_v, r) => r.assignment?.dueDate ? (
        <Text size="sm">{formatDate(r.assignment.dueDate)}</Text>
      ) : (
        <Text size="xs" c="dimmed">{r.assignment ? 'No due date' : '—'}</Text>
      ),
    },
    {
      key: 'status',
      label: 'My status',
      width: 140,
      render: (_v, r) => renderStatusBadge(r),
    },
    {
      key: 'signedDateTime',
      label: 'Signed',
      width: 130,
      render: (_v, r) => r.assignment?.signedDateTime
        ? <Text size="sm">{formatDate(r.assignment.signedDateTime)}</Text>
        : <Text size="xs" c="dimmed">—</Text>,
    },
    {
      key: 'actions',
      label: '',
      width: 40,
      render: (_v, r) => (
        <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); handleView(r.policy.policyID); }}>
          <IconEye size={16} />
        </ActionIcon>
      ),
    },
  ], [handleView]);

  const StatsCard = ({
    icon, color, value, label, filterValue,
  }: { icon: React.ReactNode; color: string; value: number; label: string; filterValue: StatsFilter }) => (
    <Card
      withBorder padding="md" radius="md"
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderColor: statsFilter === filterValue ? `var(--mantine-color-${color}-5)` : undefined,
        backgroundColor: statsFilter === filterValue ? `var(--mantine-color-${color}-light)` : undefined,
      }}
      onClick={() => handleStatsClick(filterValue)}
    >
      <Group>
        <ThemeIcon size="lg" radius="md" variant={statsFilter === filterValue ? 'filled' : 'light'} color={color}>
          {icon}
        </ThemeIcon>
        <Stack gap={0}>
          <Text size="xl" fw={700}>{value}</Text>
          <Text size="xs" c="dimmed">{label}</Text>
        </Stack>
      </Group>
    </Card>
  );

  const renderItemView = () => (
    <Stack gap="sm">
      {filtered.map((r) => (
        <Paper key={r.id} p="md" withBorder style={{ cursor: 'pointer' }} onClick={() => handleView(r.policy.policyID)}>
          <Group justify="space-between" wrap="nowrap" align="center">
            <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              <ThemeIcon size="xl" radius="md" variant="light" color={r.policy.requireSignatory ? 'orange' : 'blue'}>
                {r.policy.requireSignatory ? <IconSignature size={22} /> : <IconBookmark size={22} />}
              </ThemeIcon>
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={600} truncate>{r.policy.name ?? `Policy #${r.policy.policyID}`}</Text>
                  {r.policy.version && <Text size="xs" c="dimmed">v{r.policy.version}</Text>}
                </Group>
                {r.assignment?.dueDate && (
                  <Group gap={4}>
                    <IconCalendar size={11} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">Due {formatDate(r.assignment.dueDate)}</Text>
                  </Group>
                )}
                {r.assignment?.signedDateTime && (
                  <Text size="xs" c="dimmed">Signed {formatDate(r.assignment.signedDateTime)}</Text>
                )}
              </Stack>
            </Group>
            <Group gap="xs" wrap="nowrap">
              {renderStatusBadge(r)}
              <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); handleView(r.policy.policyID); }}>
                <IconEye size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Paper>
      ))}
    </Stack>
  );

  const renderGridView = () => (
    <Grid>
      {filtered.map((r) => (
        <Grid.Col key={r.id} span={{ base: 12, xs: 6, md: 4, lg: 3 }}>
          <Card padding="lg" withBorder style={{ cursor: 'pointer', height: '100%' }} onClick={() => handleView(r.policy.policyID)}>
            <Stack gap="sm">
              <Group justify="space-between" wrap="nowrap">
                <ThemeIcon size="lg" radius="md" variant="light" color={r.policy.requireSignatory ? 'orange' : 'blue'}>
                  {r.policy.requireSignatory ? <IconSignature size={20} /> : <IconBookmark size={20} />}
                </ThemeIcon>
                {renderStatusBadge(r)}
              </Group>
              <Stack gap={2}>
                <Text size="sm" fw={600} lineClamp={2}>{r.policy.name ?? `Policy #${r.policy.policyID}`}</Text>
                <Group gap={6}>
                  <Text size="xs" c="dimmed">#{r.policy.policyID}</Text>
                  {r.policy.version && <Text size="xs" c="dimmed">v{r.policy.version}</Text>}
                </Group>
              </Stack>
              <Divider />
              {r.assignment?.dueDate && (
                <Group gap={6} wrap="nowrap">
                  <IconCalendar size={11} color="var(--mantine-color-dimmed)" />
                  <Text size="xs" c="dimmed">Due {formatDate(r.assignment.dueDate)}</Text>
                </Group>
              )}
              {r.assignment?.signedDateTime && (
                <Text size="xs" c="dimmed">Signed {formatDate(r.assignment.signedDateTime)}</Text>
              )}
              {!r.assignment && (
                <Text size="xs" c="dimmed" fs="italic">Not assigned to you</Text>
              )}
            </Stack>
          </Card>
        </Grid.Col>
      ))}
    </Grid>
  );

  const error = aError || pError;

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between" wrap="wrap">
          <Stack gap={0}>
            <Title order={2}>My Policies</Title>
            <Text size="sm" c="dimmed">Browse all church policies, sign the ones assigned to you, and revisit your signed history.</Text>
          </Stack>
          <Group gap="sm" data-help-id="hr.my-policies.actions">
            <Tooltip label="Refresh">
              <ActionIcon variant="subtle" onClick={refresh} loading={loading}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Button component={Link} to="history" variant="default" leftSection={<IconHistory size={16} />} data-help-id="hr.my-policies.history-link">
              Signature history
            </Button>
            <PageHelpButton pageKey="hr.my-policies" />
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 2, md: 4 }} data-help-id="hr.my-policies.stats">
          <StatsCard icon={<IconHourglassEmpty size={20} />}    color="yellow" value={stats.toSign}  label="To sign"      filterValue="to-sign" />
          <StatsCard icon={<IconExclamationCircle size={20} />} color="red"    value={stats.overdue} label="Overdue"      filterValue="overdue" />
          <StatsCard icon={<IconCircleCheck size={20} />}       color="green"  value={stats.signed}  label="Signed"       filterValue="signed" />
          <StatsCard icon={<IconBookmarks size={20} />}         color="violet" value={stats.total}   label="All published" filterValue="all" />
        </SimpleGrid>

        <Paper withBorder p="md" radius="md" data-help-id="hr.my-policies.toolbar">
          <Group justify="space-between" wrap="wrap">
            <TextInput
              placeholder="Search policies…"
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ minWidth: 240, flex: 1 }}
            />
            <Group gap="sm">
              <Text size="sm" c="dimmed">
                {loading && allRows.length === 0 ? 'Loading…' : `${filtered.length} of ${allRows.length}`}
              </Text>
              <SegmentedControl
                data-help-id="hr.my-policies.view-switch"
                value={viewMode}
                onChange={(v) => setViewMode(v as PoliciesViewMode)}
                size="xs"
                data={[
                  { value: 'list', label: <Tooltip label="Table"><Group gap={0} px={4}><IconLayoutList size={14} /></Group></Tooltip> },
                  { value: 'item', label: <Tooltip label="Item"><Group gap={0} px={4}><IconLayoutRows size={14} /></Group></Tooltip> },
                  { value: 'grid', label: <Tooltip label="Grid"><Group gap={0} px={4}><IconLayoutGrid size={14} /></Group></Tooltip> },
                ]}
              />
            </Group>
          </Group>
        </Paper>

        {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>Failed to load your policies.</Alert>}

        <div data-help-id="hr.my-policies.list">
        {loading && allRows.length === 0 && viewMode !== 'list' ? (
          <Card withBorder>
            <Stack align="center" gap="xs" py="xl"><Loader /><Text size="sm" c="dimmed">Loading policies…</Text></Stack>
          </Card>
        ) : filtered.length === 0 && viewMode !== 'list' ? (
          <Card withBorder>
            <Stack align="center" gap="xs" py="xl">
              <IconShieldCheck size={32} color="var(--mantine-color-dimmed)" />
              <Text size="sm" c="dimmed">{search || statsFilter ? 'No policies match these filters.' : 'No policies are published yet.'}</Text>
            </Stack>
          </Card>
        ) : viewMode === 'list' ? (
          <Card withBorder p={0}>
            <DataTable<PolicyRow>
              columns={columns}
              data={filtered}
              loading={loading}
              enableInternalSort
              onRowClick={(row) => handleView(row.policy.policyID)}
              emptyMessage={search || statsFilter ? 'No policies match these filters.' : 'No policies are published yet.'}
              striped
              highlightOnHover
            />
          </Card>
        ) : viewMode === 'item' ? renderItemView() : renderGridView()}
        </div>
      </Stack>
    </FadeIn>
  );
}
